import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { VehicleRealtime } from "../../../../store/admin/vehiclesRealtimeStore";
import type { VehicleSocket } from "../../../../store/admin/vehiclesSocketStore";
import type { Company } from "../../../../store/admin/companiesStore";
import { useActiveTripsStore } from "../../../../store/admin/activeTripsStore";
import StatusFilters from "./StatusFilters";
import GroupFilter from "./GroupFilter";
import UnitSearch from "./UnitSearch";
import VehicleList, { useListDensity } from "./VehicleList";
import TripCard from "./TripCard";

type SidebarTab = "units" | "trips";

interface Group {
  id: string;
  name: string;
  count: number;
}

interface SidebarExpandedContentProps {
  onToggleCollapse: () => void;
  vehicles: (VehicleRealtime | VehicleSocket)[];
  unitStats: {
    driving: number;
    ralenti: number;
    stopped: number;
    offline: number;
    total: number;
  };
  groups: Group[];
  selectedStatus: string | null;
  selectedGroup: string;
  selectedCompany: string | null;
  setSelectedStatus: (status: string | null) => void;
  setSelectedGroup: (group: string) => void;
  handleStatusClick: (status: string) => void;
  isDarkMode: boolean;
  themeClasses: {
    border: string;
    text: string;
    textSecondary: string;
    cardBg: string;
    cardBorder: string;
    buttonHover: string;
    inputBg: string;
    inputBorder: string;
  };
  isReseller: boolean;
  companies?: Company[];
  loading?: boolean;
  isConnected?: boolean;
}

export default function SidebarExpandedContent({
  onToggleCollapse,
  vehicles,
  unitStats,
  groups,
  selectedStatus,
  selectedGroup,
  selectedCompany,
  setSelectedStatus,
  setSelectedGroup,
  handleStatusClick,
  isDarkMode,
  themeClasses,
  isReseller,
  loading = false,
  isConnected = true,
}: SidebarExpandedContentProps) {
  const { t } = useTranslation("lite");
  const navigate = useNavigate();
  const activeTrips = useActiveTripsStore((state) => state.trips);
  const completedTrips = useActiveTripsStore((state) => state.completedTrips);
  const markCompletedTripsSeen = useActiveTripsStore(
    (state) => state.markCompletedTripsSeen,
  );
  const [activeTab, setActiveTab] = useState<SidebarTab>("units");
  const [density, setDensity] = useListDensity();

  const inProgressTripsCount = activeTrips.filter(
    (t) => !completedTrips[t.id],
  ).length;

  useEffect(() => {
    if (activeTab === "trips") markCompletedTripsSeen();
  }, [activeTab, completedTrips, markCompletedTripsSeen]);


  return (
    <>
      {/* Header */}
      <div className={`p-3 border-b ${themeClasses.border}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className={`text-base font-bold ${themeClasses.text}`}>
              {t("sidebar.title")}
            </h2>
            {/* Estado del socket a la vista: antes sólo había un aviso ámbar dentro
                de la lista al caerse, y con la lista recorrida no se veía. */}
            {vehicles.length > 0 && (
              <span
                role="status"
                className={`inline-flex items-center gap-1.5 text-[11px] font-medium ${isConnected ? "text-primary-500" : "text-yellow-500"}`}
              >
                <span className="relative flex h-2 w-2" aria-hidden>
                  {isConnected && (
                    <span className="absolute inline-flex h-full w-full rounded-full bg-primary-500 opacity-60 motion-safe:animate-ping [animation-duration:2s]" />
                  )}
                  <span className={`relative inline-flex h-2 w-2 rounded-full ${isConnected ? "bg-primary-500" : "bg-yellow-500"}`} />
                </span>
                {isConnected ? t("sidebar.live") : t("sidebar.reconnecting")}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-1">
            <button
              type="button"
              onClick={() => setDensity(density === "compact" ? "detailed" : "compact")}
              className={`p-2 ${themeClasses.buttonHover} rounded-lg transition-colors`}
              title={t(density === "compact" ? "compactList.showDetailed" : "compactList.showCompact")}
              aria-label={t(density === "compact" ? "compactList.showDetailed" : "compactList.showCompact")}
            >
              <i
                className={`${density === "compact" ? "ri-layout-row-line" : "ri-list-check"} text-primary-500 text-sm`}
              ></i>
            </button>
            <button
              onClick={onToggleCollapse}
              className={`p-2 ${themeClasses.buttonHover} rounded-lg transition-colors`}
              title={t("sidebar.collapseSidebar")}
            >
              <i className="ri-menu-fold-line text-primary-500 text-sm"></i>
            </button>
          </div>
        </div>

        <StatusFilters
          selectedStatus={selectedStatus}
          onStatusClick={handleStatusClick}
          onClear={() => setSelectedStatus(null)}
          unitStats={unitStats}
          themeClasses={themeClasses}
        />

        <div className="space-y-2">
          {(!isReseller || selectedCompany) && <UnitSearch isDarkMode={isDarkMode} />}
          <GroupFilter
            selectedGroup={selectedGroup}
            onGroupChange={setSelectedGroup}
            groups={groups}
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
            isReseller={isReseller}
            selectedCompany={selectedCompany}
          />
        </div>

        {/* Tab Switcher: Units vs Trips - Hidden for resellers without company selection */}
        {(!isReseller || selectedCompany) && (
          <div
            className={`mt-3 flex p-1 rounded-lg border ${themeClasses.cardBorder} ${isDarkMode ? "bg-dark-card" : "bg-gray-100"}`}
            role="tablist"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "units"}
              onClick={() => setActiveTab("units")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-medium transition-colors ${
                activeTab === "units"
                  ? `bg-primary-500 text-gray-900`
                  : `${themeClasses.textSecondary} ${themeClasses.buttonHover}`
              }`}
            >
              <i className="ri-truck-line text-sm"></i>
              <span>
                {t("sidebar.tabs.units")} ({vehicles.length})
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "trips"}
              onClick={() => setActiveTab("trips")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-medium transition-colors ${
                activeTab === "trips"
                  ? `bg-primary-500 text-gray-900`
                  : `${themeClasses.textSecondary} ${themeClasses.buttonHover}`
              }`}
            >
              <i className="ri-route-line text-sm"></i>
              <span>
                {t("sidebar.tabs.trips")} ({inProgressTripsCount})
              </span>
            </button>
          </div>
        )}
      </div>

      {/* List (Units or Trips) */}
      {/* min-h-0: en una columna flex el hijo con flex-1 mide al menos su contenido,
          así que overflow-y-auto nunca se activaba; con una tarjeta expandida más alta
          que la pantalla el panel la recortaba y no había scroll (Daniel, 29-oct). */}
      <div className={`flex-1 min-h-0 overflow-y-auto ${density === "compact" ? "p-2 space-y-0.5" : "p-3 space-y-2.5"}`}>
        {/* Loading State */}
        {loading ? (
          <div className="space-y-2.5">
            {/* Skeleton Cards */}
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.cardBorder} p-3 animate-pulse`}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-10 h-10 rounded-lg ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`}
                  />
                  <div className="flex-1 space-y-2">
                    <div
                      className={`h-4 rounded ${isDarkMode ? "bg-gray-600" : "bg-gray-300"} w-3/4`}
                    />
                    <div
                      className={`h-3 rounded ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} w-1/2`}
                    />
                  </div>
                  <div
                    className={`w-16 h-6 rounded-full ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`}
                  />
                </div>
              </div>
            ))}
            <div className="flex items-center justify-center py-4">
              <div className="flex items-center space-x-2">
                <div
                  className="w-2 h-2 bg-primary-500 rounded-full animate-bounce"
                  style={{ animationDelay: "0ms" }}
                />
                <div
                  className="w-2 h-2 bg-primary-500 rounded-full animate-bounce"
                  style={{ animationDelay: "150ms" }}
                />
                <div
                  className="w-2 h-2 bg-primary-500 rounded-full animate-bounce"
                  style={{ animationDelay: "300ms" }}
                />
              </div>
              <span className={`ml-3 text-sm ${themeClasses.textSecondary}`}>
                {t("sidebar.loadingVehicles")}
              </span>
            </div>
          </div>
        ) : activeTab === "trips" ? (
          isReseller && !selectedCompany ? (
            <div className="flex flex-col items-center justify-center h-full px-6 py-8">
              <div className={`w-16 h-16 rounded-full ${isDarkMode ? "bg-dark-card" : "bg-gray-100"} flex items-center justify-center mb-4`}>
                <i className={`ri-building-2-line text-2xl ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}></i>
              </div>
              <p className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"} text-center mb-2`}>
                {t("sidebar.selectCompanyFirst")}
              </p>
              <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"} text-center`}>
                {t("sidebar.selectCompanyHint")}
              </p>
            </div>
          ) : activeTrips.length > 0 ? (
            <>
              {activeTrips.map((trip) => (
                <TripCard
                  key={trip.id}
                  trip={trip}
                  vehicles={vehicles as VehicleSocket[]}
                  isDarkMode={isDarkMode}
                  themeClasses={themeClasses}
                />
              ))}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full px-6 py-8">
              <div
                className={`w-16 h-16 rounded-full ${isDarkMode ? "bg-dark-card" : "bg-gray-100"} flex items-center justify-center mb-4`}
              >
                <i
                  className={`ri-route-line text-2xl ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                ></i>
              </div>
              <p
                className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"} text-center`}
              >
                {t("sidebar.noActiveTrips", "No hay viajes en curso")}
              </p>
            </div>
          )
        ) : vehicles.length > 0 ? (
          <>
            <VehicleList
              vehicles={vehicles as VehicleSocket[]}
              density={density}
              isDarkMode={isDarkMode}
              themeClasses={themeClasses}
              selectedStatus={selectedStatus}
            />
          </>
        ) : isReseller && !selectedCompany ? (
          <div className="flex flex-col items-center justify-center h-full px-6 py-8">
            <div className={`w-16 h-16 rounded-full ${isDarkMode ? "bg-dark-card" : "bg-gray-100"} flex items-center justify-center mb-4`}>
              <i className={`ri-building-2-line text-2xl ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}></i>
            </div>
            <p className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"} text-center mb-2`}>
              {t("sidebar.selectCompanyFirst")}
            </p>
            <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"} text-center`}>
              {t("sidebar.selectCompanyHint")}
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full px-6 py-8">
            <div className={`w-16 h-16 rounded-full ${isDarkMode ? "bg-dark-card" : "bg-gray-100"} flex items-center justify-center mb-4`}>
              <i className={`ri-car-line text-2xl ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}></i>
            </div>
            <p className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"} text-center`}>
              {t("sidebar.noUnits")}
            </p>
          </div>
        )}
      </div>

      {/* Footer: solo en Viajes, donde lleva el enlace a la lista completa. En
          Unidades repetía el total ("Total: 10 Unidades") y el grupo, que ya
          muestran la pestaña "Todos", "Unidades (N)" y el selector de grupo. */}
      {activeTab === "trips" && (
        <div
          className={`p-3 border-t ${themeClasses.border} ${themeClasses.cardBg}`}
        >
          <div className="text-center">
            <p className={`text-xs ${themeClasses.text} font-medium`}>
              {t("sidebar.totalActiveTrips", {
                count: inProgressTripsCount,
                defaultValue: `Total: ${inProgressTripsCount} Viajes en curso`,
              })}
            </p>
            <button
              onClick={() => navigate("/admin/trips")}
              className={`text-xs ${themeClasses.textSecondary} hover:text-primary-500 underline cursor-pointer`}
            >
              {t("sidebar.viewAllTrips", "Ver todos los viajes")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
